import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Pencil, Plus } from "lucide-react";

import { getEntities } from "src/api/settings";
import useHasPermissions from "src/hooks/useHasPermissions";
import CustomModal from "src/components/CustomModal";
import CreateWarehouse from "src/components/modals/CreateWarehouse";
import PermissionWrapper from "src/components/PermissionWrapper";
import { Button } from "src/components/ui/button";
import { DataTable, SettingsPanel, type Column, type RowsPerPageOption } from "src/components/common";

interface EntityRow {
	uuid: string;
	name?: string;
	type?: string;
	first_name?: string;
	last_name?: string;
}

const WarehouseSettings = () => {
	const canEdit = useHasPermissions("update_entity");
	const [searchParams, setSearchParams] = useSearchParams();

	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);

	const { data, isLoading } = useQuery({
		queryKey: ["entities", page, limit],
		queryFn: () => getEntities({ page, limit: limit === "All" ? 9999 : limit }),
	});

	const list = data?.data;
	const entities = list?.data ?? [];

	const columns: Column<EntityRow>[] = [
		{ key: "name", header: "Name", primary: true, cell: (row) => <span className="tw-capitalize">{row.name ?? "—"}</span> },
		{
			key: "manager",
			header: "Manager",
			cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "N/A",
		},
		{ key: "type", header: "Type", cell: (row) => <span className="tw-capitalize">{row.type ?? "—"}</span> },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[70px] tw-text-right",
			cell: (row) =>
				canEdit ? (
					<CustomModal
						title="Edit Warehouse/Storefront"
						trigger={
							<button
								type="button"
								aria-label="Edit"
								className="tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
							>
								<Pencil className="tw-h-3.5 tw-w-3.5" />
							</button>
						}
					>
						<CreateWarehouse entity={row} />
					</CustomModal>
				) : null,
		},
	];

	return (
		<SettingsPanel
			title="Branches & Warehouses"
			description="Locations that stock and sales are scoped to."
			actions={
				<PermissionWrapper permission="add_entity">
					<CustomModal
						title="Create Warehouse/Storefront"
						trigger={
							<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
								<Plus className="tw-h-4 tw-w-4" />
								Create
							</Button>
						}
					>
						<CreateWarehouse />
					</CustomModal>
				</PermissionWrapper>
			}
			className="tw-overflow-hidden"
		>
			<DataTable
				data={entities}
				columns={columns}
				rowKey={(row) => row.uuid}
				loading={isLoading}
				totalRows={list?.total ?? entities.length}
				page={page}
				onPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("page", String(next));
					setSearchParams(params);
				}}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				emptyTitle="No branches yet"
				emptyDescription="Create a storefront or warehouse to scope stock and sales to a location."
			/>
		</SettingsPanel>
	);
};

export default WarehouseSettings;
